import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIdCardDownload } from "@/hooks/useIdCardDownload";
import { IdCardItem } from "./id-card/IdCardItem";

interface IdCard {
  id: string;
  file_path: string;
  uploaded_at: string;
}

interface IdCardListProps {
  contactId: string;
}

export const IdCardList = ({ contactId }: IdCardListProps) => {
  const { toast } = useToast();
  const [idCards, setIdCards] = useState<IdCard[]>([]);
  const { downloadIdCard } = useIdCardDownload();

  const fetchIdCards = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No authenticated session"
      });
      return;
    }

    const { data, error } = await supabase
      .from('id_cards')
      .select('*')
      .eq('user_id', contactId);

    if (error) {
      console.error("Error fetching ID cards:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch ID cards"
      });
      return;
    }

    setIdCards(data || []);
  };

  useEffect(() => {
    fetchIdCards();
  }, [contactId]);

  const handleDelete = async (id: string, filePath: string) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('id_cards')
        .remove([filePath]);

      if (storageError) {
        throw storageError;
      }

      const { error: dbError } = await supabase
        .from('id_cards')
        .delete()
        .eq('id', id);

      if (dbError) {
        throw dbError;
      }

      toast({
        title: "Success",
        description: "ID card deleted successfully"
      });

      fetchIdCards();
    } catch (error: any) {
      console.error('Error deleting ID card:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete ID card"
      });
    }
  };

  return (
    <div className="space-y-4">
      {idCards.map((card) => (
        <IdCardItem
          key={card.id}
          id={card.id}
          uploadDate={card.uploaded_at}
          onDownload={() => downloadIdCard(card.file_path)}
          onDelete={() => handleDelete(card.id, card.file_path)}
        />
      ))}
    </div>
  );
};